import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const TestimonialCarousel = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const videos = [
    { id: 'george', name: 'George', src: '/videos/george.mp4', poster: '/videos/george.jpg' },
    { id: 'alex', name: 'Alex', src: '/videos/alex.mp4', poster: '/videos/alex.jpg' },
    { id: 'mark', name: 'Mark', src: '/videos/mark.mp4', poster: '/videos/mark.jpg' },
    { id: 'josh', name: 'Josh', src: '/videos/josh.mp4', poster: '/videos/josh.jpg' },
    { id: 'chino', name: 'Chino', src: '/videos/chino.mp4', poster: '/videos/chino.jpg' },
    { id: 'bartelles', name: 'Bartelles', src: '/videos/bartelles.mp4', poster: '/videos/bartelles.jpg' },
    { id: 'cynthia', name: 'Cynthia', src: '/videos/cynthia.mp4', poster: '/videos/cynthia.jpg' },
    { id: 'brandon', name: 'Brandon', src: '/videos/brandon.mp4', poster: '/videos/brandon.jpg' },
  ];

  // Double the videos for a seamless infinite loop
  const duplicatedVideos = [...videos, ...videos];

  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
      setIsPaused(true);
    } else {
      document.body.style.overflow = 'unset';
      setIsPaused(false);
    }
  }, [selectedVideo]);

  return (
    <>
      <section className="py-16 md:py-24 border-b border-white/5 bg-[#0B0F19] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-6 mb-10 md:mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white tracking-tight text-center"
          >
            Hear from our customers
          </motion.h2>
        </div>

        <div 
          className="relative w-full"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => !selectedVideo && setIsPaused(false)}
        >
          {/* Marquee Container */}
          <motion.div 
            className="flex gap-4 md:gap-6 px-4 md:px-6 cursor-grab active:cursor-grabbing"
            animate={{
              x: isPaused ? undefined : [0, -1 * (videos.length * (320 + 24))]
            }}
            transition={{
              x: {
                duration: 40, // Adjust speed here (higher = slower)
                repeat: Infinity,
                ease: "linear",
              }
            }}
          >
            {duplicatedVideos.map((v, i) => (
              <div 
                key={`${v.id}-${i}`}
                onClick={() => setSelectedVideo(v)}
                className="shrink-0 w-[72vw] max-w-[260px] sm:w-[260px] md:w-[320px] md:max-w-none aspect-[9/16] rounded-2xl md:rounded-3xl overflow-hidden bg-[#141822] border border-white/5 shadow-2xl relative group cursor-pointer"
              >
                <div className="absolute inset-0 w-full h-full bg-slate-800 flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiFilm} className="text-3xl text-slate-600" />
                </div>

                <video
                  src={v.src}
                  poster={v.poster}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover:bg-white/30 group-hover:scale-110 transition-all shadow-xl">
                    <SafeIcon icon={FiIcons.FiPlay} className="text-2xl text-white ml-1" />
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Fade Edges for smoother look */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0B0F19] to-transparent pointer-events-none z-10 hidden md:block"></div>
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0B0F19] to-transparent pointer-events-none z-10 hidden md:block"></div>
        </div>
      </section>

      {/* Full Screen Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedVideo(null)}
            className="fixed inset-0 z-[100] bg-[#0B0F19]/95 backdrop-blur-xl flex items-center justify-center p-3 sm:p-4 md:p-12"
          >
            <button 
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 md:top-8 md:right-8 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 hover:scale-110 transition-all z-[110]"
            >
              <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
            </button>

            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl max-h-[82vh] md:max-h-[85vh] bg-black rounded-xl md:rounded-2xl shadow-[0_0_100px_rgba(0,0,0,0.8)] overflow-hidden border border-white/10 flex items-center justify-center"
            >
              <video
                src={selectedVideo.src}
                poster={selectedVideo.poster}
                className="w-full max-h-[82vh] md:max-h-[85vh] object-contain"
                controls
                autoPlay
                playsInline
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                title="Customer Testimonial Fullscreen"
              >
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TestimonialCarousel;
